import {NoteIcon} from '@primer/octicons-react'
import {Button} from '@primer/react'
import {announce} from '@github-ui/aria-live'
import {useMarkReadyForReviewMutation} from '@github-ui/mergebox/use-mark-ready-for-review-mutation'
import type {ViewerRole} from '../deciders/use-decide-next-action'

import {ForYouItem} from '../shared/ForYouItem'
import {ForYouLeadingIcon} from '../shared/ForYouLeadingIcon'
import {FailureDialog} from '../../sidebar-actions/FailureDialog'
import {useLayoutRouteData} from '../../../../page-data/loaders/use-layout-route-data'
import {useOverviewRouteData} from '../../data/use-overview-route-data'
import {useInvalidatePrState} from '../../data/use-invalidate-pr-state'
import {PullRequestState} from '../../../../types'
import {usePullRequestAnalytics} from '../../../../hooks/use-pull-request-analytics'

export function shouldShowDraftPR(pullRequestState: PullRequestState) {
  return pullRequestState === PullRequestState.Draft
}

export function DraftPR({variant, userRole}: {variant?: 'default' | 'embedded'; userRole?: ViewerRole}) {
  const {pullRequest} = useLayoutRouteData()
  const {user} = useOverviewRouteData()
  const invalidatePrState = useInvalidatePrState()
  const {sendPullRequestAnalyticsEvent} = usePullRequestAnalytics()

  const markReadyForReviewMutation = useMarkReadyForReviewMutation({
    onSuccess: () => {
      announce('Pull request marked as ready for review')
      invalidatePrState()
    },
  })

  if (!shouldShowDraftPR(pullRequest.state)) {
    return null
  }

  const canMarkReadyForReview = !!user?.canMarkReadyForReview

  if (!canMarkReadyForReview || userRole === 'reviewer') {
    return (
      <ForYouItem
        leadingVisual={<ForYouLeadingIcon icon={NoteIcon} variant="warning" />}
        title="Draft"
        itemName="draft-pr"
        variant={variant}
        description="This pull request is not ready for review yet."
      />
    )
  }

  const handleMarkReadyForReview = () => {
    if (!canMarkReadyForReview || markReadyForReviewMutation.isPending) return
    sendPullRequestAnalyticsEvent('overview.for_you_item_action', 'OVERVIEW_FOR_YOU_ITEM', {
      item_name: 'draft-pr',
      action_name: 'mark_ready_for_review',
    })
    markReadyForReviewMutation.mutate()
  }

  return (
    <>
      <ForYouItem
        leadingVisual={<ForYouLeadingIcon icon={NoteIcon} variant="warning" />}
        title="Mark ready for review"
        itemName="draft-pr"
        variant={variant}
        actionSlot={
          <Button
            size="small"
            onClick={handleMarkReadyForReview}
            loading={markReadyForReviewMutation.isPending}
            loadingAnnouncement="Marking ready for review"
          >
            Ready for review
          </Button>
        }
      />
      {markReadyForReviewMutation.isError && (
        <FailureDialog
          title="Failed to mark ready for review"
          message="An error occurred while marking this pull request as ready for review. Please try again."
          details={markReadyForReviewMutation.error.message}
          onRetry={() => markReadyForReviewMutation.mutate()}
          onDismiss={() => markReadyForReviewMutation.reset()}
        />
      )}
    </>
  )
}
